import { FC } from 'react'
import { Box, Stack, Tooltip, Typography, StackProps, IconButton } from '@mui/material'
import CopyIcon from '../../../../assets/images/copy-icon.svg?react'
import { useSnackbar } from '../../../../hooks/useSnackbar'
import { truncatedEngagementId } from '../ertUtils'

type EngagementTagDetailsProps = StackProps & {
  details: string[] // This is an array since we may need to show multiple individual names on the specialists tag; other tags will likely have one value in the array
  shouldShowCopyIcon: boolean
  shouldTruncateDetail: boolean
  icon?: React.ReactNode
  copyLabel?: string
}

export const EngagementTagDetails: FC<EngagementTagDetailsProps> = ({
  details,
  shouldShowCopyIcon,
  shouldTruncateDetail,
  icon,
  copyLabel,
  ...stackProps
}) => {
  const { toast } = useSnackbar()

  const getDisplayValue = (detail: string): string => {
    return shouldTruncateDetail ? truncatedEngagementId(detail) : detail
  }

  const handleCopyClick = () => {
    navigator.clipboard.writeText(details[0])
    toast(`${copyLabel} copied to clipboard`, { variant: 'success' })
  }

  return (
    <Stack direction="row" gap="var(--spacing-2xs)" flexWrap="nowrap" {...stackProps}>
      {details.map((detail, index) => (
        <Stack
          key={index}
          direction="row"
          sx={{
            alignItems: 'center',
            gap: '0.125rem',
            padding: '0 var(--spacing-2xs)',
            borderRadius: 'var(--border-radius-m)',
            background: 'var(--bg-surface-medium)'
          }}
        >
          {icon && <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>}
          <Typography>{getDisplayValue(detail)}</Typography>
        </Stack>
      ))}
      {shouldShowCopyIcon && (
        <Tooltip title={`Copy ${copyLabel}`} arrow>
          <IconButton
            aria-label={`Copy ${copyLabel}`}
            onClick={handleCopyClick}
            sx={{
              fontSize: 'var(--size-xl)',
              padding: 'var(--spacing-none)'
            }}
          >
            <CopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}
